/**
 * Notification Store
 *
 * Manages notification state, pagination, read/unread counts, and user notification
 * preferences. Supports paginated fetching, mark-as-read (individual and bulk),
 * deletion, and preference updates for controlling which notification categories
 * the user receives.
 *
 * @module useNotificationStore
 *
 * @example
 * ```tsx
 * import { useNotificationStore, selectUnreadCount } from '@/store/useNotificationStore'
 *
 * function NotificationBell() {
 *   const { fetchCounts, markAllAsRead } = useNotificationStore()
 *   const unread = useNotificationStore(selectUnreadCount)
 *
 *   useEffect(() => { fetchCounts() }, [])
 *
 *   return (
 *     <button onClick={markAllAsRead}>
 *       Notifications {unread > 0 && <Badge count={unread} />}
 *     </button>
 *   )
 * }
 * ```
 */

import { createWithEqualityFn } from 'zustand/traditional'
import { shallow } from 'zustand/shallow'
import type {
  Notification,
  NotificationPreferences,
  NotificationCounts,
  UUID,
} from '../types'
import { notificationApi } from '../api/notification.api'

interface NotificationState {
  // Notifications
  notifications: Notification[]
  counts: NotificationCounts
  hasMore: boolean
  currentPage: number

  // Preferences
  preferences: NotificationPreferences | null

  // Loading states
  isLoading: boolean
  isLoadingMore: boolean
  isSaving: boolean

  // Error state
  error: string | null

  // Actions
  fetchNotifications: (page?: number) => Promise<void>
  fetchMoreNotifications: () => Promise<void>
  fetchCounts: () => Promise<void>
  markAsRead: (notificationId: UUID) => Promise<void>
  markAllAsRead: () => Promise<void>
  deleteNotification: (notificationId: UUID) => Promise<void>
  fetchPreferences: () => Promise<void>
  updatePreferences: (updates: Partial<NotificationPreferences>) => Promise<boolean>
  clearError: () => void
  reset: () => void
}

const initialState = {
  notifications: [] as Notification[],
  counts: { total: 0, unread: 0, byCategory: {} } as NotificationCounts,
  hasMore: false,
  currentPage: 1,
  preferences: null as NotificationPreferences | null,
  isLoading: false,
  isLoadingMore: false,
  isSaving: false,
  error: null as string | null,
}

export const useNotificationStore = createWithEqualityFn<NotificationState>()((set, get) => ({
  ...initialState,

  fetchNotifications: async (page = 1) => {
    set({ isLoading: true, error: null })

    const response = await notificationApi.getNotifications(page, 20)

    if (response.success && response.data) {
      set({
        notifications: response.data,
        hasMore: response.pagination?.hasMore ?? false,
        currentPage: page,
        isLoading: false,
      })
    } else {
      set({
        error: 'Failed to load notifications',
        isLoading: false,
      })
    }
  },

  fetchMoreNotifications: async () => {
    const { currentPage, hasMore, notifications } = get()
    if (!hasMore) return

    set({ isLoadingMore: true })

    const response = await notificationApi.getNotifications(currentPage + 1, 20)

    if (response.success && response.data) {
      set({
        notifications: [...notifications, ...response.data],
        hasMore: response.pagination?.hasMore ?? false,
        currentPage: currentPage + 1,
        isLoadingMore: false,
      })
    } else {
      set({ isLoadingMore: false })
    }
  },

  fetchCounts: async () => {
    const response = await notificationApi.getNotificationCounts()

    if (response.success && response.data) {
      set({ counts: response.data })
    }
  },

  markAsRead: async (notificationId) => {
    const response = await notificationApi.markAsRead(notificationId)

    if (response.success && response.data) {
      set({
        notifications: get().notifications.map((n) =>
          n.id === notificationId ? response.data! : n
        ),
        counts: {
          ...get().counts,
          unread: Math.max(0, get().counts.unread - 1),
        },
      })
    }
  },

  markAllAsRead: async () => {
    const response = await notificationApi.markAllAsRead()

    if (response.success) {
      set({
        notifications: get().notifications.map((n) => ({
          ...n,
          isRead: true,
          readAt: new Date().toISOString(),
        })),
        counts: {
          ...get().counts,
          unread: 0,
          byCategory: {},
        },
      })
    }
  },

  deleteNotification: async (notificationId) => {
    const notification = get().notifications.find((n) => n.id === notificationId)
    const response = await notificationApi.deleteNotification(notificationId)

    if (response.success) {
      set({
        notifications: get().notifications.filter((n) => n.id !== notificationId),
        counts: {
          ...get().counts,
          total: get().counts.total - 1,
          unread: notification && !notification.isRead
            ? get().counts.unread - 1
            : get().counts.unread,
        },
      })
    }
  },

  fetchPreferences: async () => {
    const response = await notificationApi.getPreferences()

    if (response.success && response.data) {
      set({ preferences: response.data })
    }
  },

  updatePreferences: async (updates) => {
    set({ isSaving: true, error: null })

    const response = await notificationApi.updatePreferences(updates)

    if (response.success && response.data) {
      set({ preferences: response.data, isSaving: false })
      return true
    } else {
      set({
        error: response.error ?? 'Failed to update preferences',
        isSaving: false,
      })
      return false
    }
  },

  clearError: () => set({ error: null }),

  reset: () => set(initialState),
}))

// ============================================================================
// Selectors
// ============================================================================

/** Select the paginated list of notifications. */
export const selectNotifications = (state: NotificationState) => state.notifications

/** Select the count of unread notifications. */
export const selectUnreadCount = (state: NotificationState) => state.counts.unread

/** Select full notification counts (total, unread, by category). */
export const selectNotificationCounts = (state: NotificationState) => state.counts

/** Select the user's notification delivery preferences. */
export const selectPreferences = (state: NotificationState) => state.preferences

// ============================================================================
// Grouped Notifications
// ============================================================================

interface CategoryGroup {
  category: string
  notifications: Notification[]
  count: number
}

export interface DateGroup {
  dateLabel: string
  categories: CategoryGroup[]
}

/**
 * Classify a date string into a human-readable date bucket.
 * Returns "Today", "Yesterday", "This Week", or "Older".
 */
function getDateLabel(dateStr: string): string {
  const date = new Date(dateStr)
  const now = new Date()

  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const startOfYesterday = new Date(startOfToday.getTime() - 86_400_000)
  const startOfWeek = new Date(startOfToday.getTime() - startOfToday.getDay() * 86_400_000)

  if (date >= startOfToday) return 'Today'
  if (date >= startOfYesterday) return 'Yesterday'
  if (date >= startOfWeek) return 'This Week'
  return 'Older'
}

/** Date label ordering for deterministic sort. */
const DATE_LABEL_ORDER: Record<string, number> = {
  Today: 0,
  Yesterday: 1,
  'This Week': 2,
  Older: 3,
}

/**
 * Select notifications grouped first by date bucket, then by category.
 *
 * Returns an array of `DateGroup` objects sorted chronologically (newest first).
 * Within each date group, categories are sorted alphabetically and each includes
 * a count of notifications.
 */
export const selectGroupedNotifications = (state: NotificationState): DateGroup[] => {
  const { notifications } = state

  // Build a two-level map: dateLabel -> category -> notifications[]
  const dateMap = new Map<string, Map<string, Notification[]>>()

  for (const n of notifications) {
    const label = getDateLabel(n.createdAt)

    let catMap = dateMap.get(label)
    if (!catMap) {
      catMap = new Map<string, Notification[]>()
      dateMap.set(label, catMap)
    }

    const list = catMap.get(n.category)
    if (list) {
      list.push(n)
    } else {
      catMap.set(n.category, [n])
    }
  }

  // Convert map to sorted array
  const groups: DateGroup[] = []

  for (const [dateLabel, catMap] of dateMap) {
    const categories: CategoryGroup[] = []

    for (const [category, items] of catMap) {
      categories.push({ category, notifications: items, count: items.length })
    }

    categories.sort((a, b) => a.category.localeCompare(b.category))
    groups.push({ dateLabel, categories })
  }

  groups.sort((a, b) => (DATE_LABEL_ORDER[a.dateLabel] ?? 99) - (DATE_LABEL_ORDER[b.dateLabel] ?? 99))

  return groups
}
